import type { IProductsGetResponse } from '../../definitions'

import { String } from 'stdlib/String'
import { Array }  from 'stdlib/Array'
import { URL }    from 'stdlib/URL'
import { is }     from 'stdlib/is'

import scroll from '@common/scroll/scroll'

import ajax       from '../../ajax'
import { ERoute } from '../../definitions'

import router from '../../router'

import updateStyleContent      from './updateStyleContent'
import getProductFilterQueries from './getProductFilterQueries'

const CATEGORY_SECTION_STYLE_ID = 'category-products-style'

const MAX_IMAGE_COUNT = 4

/**
 * Kategória név alapján termékek megjelenítése.
 * @param categoryName - A kategória neve.
 */
export default async function renderCategoryProducts (categoryName?: string): Promise<void> {
  const container = document.body.querySelector('#category-products.categories-section__products-container')

  if (!container) {
    return
  }

  const template = document.body.querySelector('template#category-product-template')

  if (!template) {
    return
  }

  const imageTemplate = document.body.querySelector<HTMLTemplateElement>(
    'template#category-product-small-image-template'
  )

  if (!imageTemplate) {
    return
  }

  const queries = getProductFilterQueries(categoryName)

  const url = URL.make(ajax.routes.product.GetProducts, queries)

  const { isSuccess, result } = await ajax.send(url)

  if (!isSuccess) {
    return
  }

  const { products } = result as IProductsGetResponse

  const fragment = document.createDocumentFragment()

  // Nincs megjeleníthető termék
  if (is.empty(products)) {
    const notFoundProductTemplate = document.body.querySelector<HTMLTemplateElement>(
      'template#category-not-found-product-template'
    )

    if (!notFoundProductTemplate) {
      return
    }

    const elem = document.createElement('div')

    elem.classList.add('categories-section__product')
    elem.classList.add('categories-section__product--not-found')

    elem.innerHTML = notFoundProductTemplate.innerHTML

    fragment.appendChild(elem)
  }
  else {
    const productImageCounts = products.map((product) => {
      return product.images.length
    })

    const maxImageCount = Math.max(...productImageCounts)

    // Termék elemek generálása.
    products.forEach((product, i) => {
      const productElem = document.createElement('div')

      productElem.classList.add('categories-section__product')

      // Termékhez tartozó videó elem
      if (product.youtubeLink) {
        const largeVideo = document.createElement('imm-video')

        largeVideo.classList.add('categories-section__product-large-video')
        largeVideo.setAttribute('youtube-id', product.youtubeLink)

        productElem.appendChild(largeVideo)
      }

      // Termék nagy képek
      product.images.forEach((image, j) => {
        const img = document.createElement('img')

        img.classList.add('categories-section__product-large-image')

        img.src = `https://wsrv.nl?output=webp&url=${ image }`
        img.alt = `${ product.name } termék képe`
        img.setAttribute('data-index', `${ j + 1 }`)

        img.loading = 'lazy'

        productElem.appendChild(img)
      })

      productElem.innerHTML = productElem.innerHTML + template.innerHTML

      const productImages = productElem.querySelector('.categories-section__product-images')

      if (!productImages) {
        return
      }

      // Termék videó kis kép
      if (product.youtubeLink) {
        const videoTemplate = document.body.querySelector('template#category-product-small-video-template')

        if (videoTemplate) {
          productImages.innerHTML = videoTemplate.innerHTML

          const input = productImages.querySelector<HTMLInputElement>('.categories-section__product-small-video-input')

          if (input) {
            input.name = `category-product-image-${ i }`
            input.id = `category-product-video-${ i }`
          }

          const label = productImages.querySelector<HTMLLabelElement>(
            '.categories-section__product-small-video-container'
          )

          if (label) {
            label.htmlFor = `category-product-video-${ i }`
          }

          const smallVideoImage = productElem.querySelector<HTMLImageElement>(
            'img.categories-section__product-small-image'
          )

          if (smallVideoImage) {
            smallVideoImage.src = `https://wsrv.nl?output=webp&url=https://img.youtube.com/vi/${  product.youtubeLink  }/hqdefault.jpg`
            smallVideoImage.alt = `${ product.name } - videó`
          }
        }
      }

      const visibleImages = product.youtubeLink
        ? product.images.slice(0, MAX_IMAGE_COUNT - 1)
        : product.images.slice(0, MAX_IMAGE_COUNT)

      // Termék kis képek generálása
      visibleImages.forEach((image, j) => {
        const clone = imageTemplate.content.cloneNode(true) as HTMLElement

        const input = clone.querySelector<HTMLInputElement>('.categories-section__product-small-image-input')

        if (input) {
          input.name = `category-product-image-${ i }`
          input.id = `category-product-image-${ i }-${ j + 1 }`
          input.setAttribute('data-index', `${ j + 1 }`)

          input.checked = is.null(product.youtubeLink) && j === 0
        }

        const label = clone.querySelector<HTMLLabelElement>('.categories-section__product-small-image-container')

        if (label) {
          label.htmlFor = `category-product-image-${ i }-${ j + 1 }`
        }

        const img = clone.querySelector<HTMLImageElement>('.categories-section__product-small-image')

        if (img) {
          img.src = `https://wsrv.nl?output=webp&url=${ image }`
          img.alt = `${ product.name } - ${ j + 1 }`
        }

        productImages.appendChild(clone)
      })

      // Termék neve
      const title = productElem.querySelector('h3.categories-section__product-title')

      if (title) {
        title.textContent = product.name
      }

      // Termék leírása
      const description = productElem.querySelector('p.categories-section__product-description')

      if (description) {
        description.innerHTML = product.shortDescription
      }

      // Termék bruttó ára
      const grossPrice = productElem.querySelector('p.price__gross-price')

      if (grossPrice) {
        grossPrice.textContent = String.formatCurrency(product.grossPrice)
      }

      // Termék nettó ára
      const netPrice = productElem.querySelector('p.price__net-price')

      if (netPrice) {
        netPrice.textContent = `(${ String.formatCurrency(product.netPrice) } + ÁFA)`
      }

      const detailsLink = productElem.querySelector<HTMLAnchorElement>('a.categories-section__product-details')

      if (detailsLink) {
        const url = URL.createRoute(ERoute.Product, [ product.sku, String.slugify(product.name) ])

        detailsLink.href = url

        detailsLink.addEventListener('click', (e) => {
          e.preventDefault()

          router.push(url)

          scroll.toY(undefined, 'instant')
        })
      }

      fragment.appendChild(productElem)

      const button = productElem.querySelector<HTMLAnchorElement>('a.categories-section__product-button')

      if (button) {
        button.href = product.link

        // Előrendelhető vagy vásárolható a termék
        button.textContent = product.stock === 0
          ? 'Előrendelhető'
          : 'Vásárlás'
      }
    })

    const content = `
      ${ Array.times(maxImageCount, (i) => {
        const selector = [
          `.categories-section__product-large-image[data-index="${ i + 1 }"]`,
          ':has(~ .categories-section__product-images input.categories-section__product-small-image-input',
          `[data-index="${ i + 1 }"]:checked)`
        ].join('')

        return `
          ${ selector } {
            display: block;
          }
        `
      }).join('\n') }
    `

    updateStyleContent(CATEGORY_SECTION_STYLE_ID, content)
  }

  container.innerHTML = ''

  container.appendChild(fragment)
}
